import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from 'store';
import {
	shadingRecalcAccepted, syncShadingState, shadingFailed, resetDsmShading, resumeShadingWatch,
	shadingRecalcRequested,
} from 'store/slices/DsmShadingSlice';
import { changePanel, fetchRoofData } from 'store/slices/RoofDataSlice/roofDataSlice';
import type { TransformedSolarData } from 'store/slices/RoofDataSlice/types';
import { SHADING_STATUS } from 'constants/index';
import {
	markShadingStart, markShadingComplete, markNoShadingRunExpected, resetDesignPipelineTiming,
} from 'utils/designLoadTiming';
import { isInHouseSimulationMode } from '../../util';
import {
	getShadingStatus,
	recalculateShading,
	ShadingStatusSnap,
} from 'api/editDsm';

const POLL_INTERVAL_MS = 1000;
const MAX_CONSECUTIVE_POLL_ERRORS = 40; // ~120s of failed polls → give up and surface the error

// A run takes ~10s. Far above that, so this only ever catches a job that died mid-session.
const MAX_WATCH_MS = 5 * 60 * 1000;

// Both fetchRoofData/changePanel resolve to TransformedSolarData → shadingAnalysis is at payload.roofData.
function readShadingAnalysis(payload: unknown): ShadingAnalysis | undefined {
	return (payload as TransformedSolarData | undefined)?.roofData?.shadingAnalysis;
}

const isWatchableStatus = (s: ShadingStatus) => s === SHADING_STATUS.QUEUED || s === SHADING_STATUS.RUNNING;

// NOTE: created UNTYPED on purpose. Parametrising with <RootState, AppDispatch> here would
export const dsmShadingListener = createListenerMiddleware();

/**
 * The DSM shading recompute lifecycle, owned entirely by the store (no component host).
 * Started by the "Done" action (`shadingRecalcAccepted`) or by `resumeShadingWatch` (re-attach
 * after a reload/deploy). Polls `edit-dsm/status/:uuid` while the run is QUEUED/RUNNING and stops
 * on COMPLETED/FAILED, refetching the design when COMPLETED + inSync. Only the newest watch runs
 * (`cancelActiveListeners`), so the loop needs no idempotency guards.
 *
 * NOT started by a create 202 any more. The build's own first run is reported by the create-status
 * poll (designCreateListener) while the build is still going, and handed to the load listener below
 * once the design lands in the store — so this loop always has a design to refetch and never has to
 * ask whether it is running ahead of one.
 */
dsmShadingListener.startListening({
	matcher: isAnyOf(shadingRecalcRequested, shadingRecalcAccepted, resumeShadingWatch),
	effect: async (action, api) => {
		api.cancelActiveListeners(); // only the newest recompute-watch runs at a time

		const getState = () => api.getState() as RootState;
		const dispatch = api.dispatch as AppDispatch;

		// Cancel-only cue: supersede the incumbent watch NOW, and let the 202 start the
		// replacement. Cancelling only on the 202 would leave the old poll free to report the
		// OLD run's completion (refetching stale production, dropping the spinner) during the
		// POST round-trip.
		if (shadingRecalcRequested.match(action)) {
			// A run is enqueued (202) or being re-attached → start of the shading phase of the
			// create → fetch → shading timer (no-op if it wasn't armed by a create-design POST).
			markShadingStart({ status: getState().roofData.data.shadingAnalysis?.shadingStatus });
			return;
		}
		// Nothing was enqueued — don't poll for a bake that isn't coming.
		if (shadingRecalcAccepted.match(action) && !action.payload.accepted) return;

		const uuid = getState().roofData.data.uuid;
		if (!uuid) return;

		// re-run recommendation if it is first shading analysis
		// i.e: when no flux data
		const isFirstShadingRun = !getState().roofData.data.shadingAnalysis?.annualFluxUrl;

		let consecutiveErrors = 0;
		const startedAt = Date.now();

		// eslint-disable-next-line no-constant-condition
		while (true) {
			await api.delay(POLL_INTERVAL_MS);

			let snap: ShadingStatusSnap;
			try {
				snap = await getShadingStatus(uuid);
				consecutiveErrors = 0;
			} catch {
				if (++consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
					dispatch(shadingFailed({ message: 'Lost track of the shading recompute' }));
					return;
				}
				continue;
			}

			dispatch(syncShadingState(snap));

			if (snap.shadingStatus === SHADING_STATUS.FAILED) {
				resetDesignPipelineTiming(); // never completes → nothing to report
				dispatch(shadingFailed({ message: 'Shading recompute failed' }));
				return;
			}

			if (snap.shadingStatus === SHADING_STATUS.COMPLETED) {
				// terminal milestone of the create → fetch → shading timer (no-op if it wasn't armed)
				markShadingComplete({ status: SHADING_STATUS.COMPLETED, inSync: snap.inSync });
				if (snap.inSync) {
					// fresh production is ready → refetch the design to pick up the new numbers
					const { panel, designType } = getState().roofData.data;
					dispatch(changePanel({
						uuid,
						panelKey: panel?.key ?? '',
						designType,
						retainPanelSelectionOnUi: !isFirstShadingRun,
						refetchIrradiance: true,
						retainDrawingSessionOnUi: true,
					}));
				}
				// !inSync → the run finished but the user edited since, so this design is dirty exactly
				// like PENDING. Stop watching and wait for their next Done — do NOT auto-fire a run
				// they didn't ask for (it would also take away editing several times before recomputing).
				return;
			}

			// PENDING = no run requested, so nothing is coming.
			if (snap.shadingStatus === SHADING_STATUS.PENDING) return;

			// QUEUED/RUNNING → keep waiting, but not forever: if the job dies mid-session (its Redis
			// queue wiped while Postgres still says QUEUED) the status never changes again and this
			// would poll for the lifetime of the tab. Recovery for that is re-opening the design,
			// which re-requests the run — see the load listener below.
			if (Date.now() - startedAt >= MAX_WATCH_MS) {
				dispatch(shadingFailed({ message: 'Shading recompute timed out' }));
				return;
			}
		}
	},
});

/**
 * A design that loads as QUEUED/RUNNING has a run the server believes is in flight — but the API
 * records that in Postgres while the job itself lives in Redis, so a Redis restart/wipe (or a
 * replaced instance) can lose the job while the DB still says QUEUED. Nothing then ever runs it,
 * and the in-progress guard 409s every edit, so the design is wedged.
 *
 * Once per design load — never on a poll tick. Gated on QUEUED/RUNNING so a merely-dirty design
 * (PENDING, or COMPLETED + !inSync: the normal state after edits with no Done) never triggers a
 * recompute nobody asked for.
 */
dsmShadingListener.startListening({
	matcher: isAnyOf(fetchRoofData.fulfilled, changePanel.fulfilled),
	effect: async (action, api) => {
		const state = api.getState() as RootState;
		if (state.dsmShading.data.isRecomputing) return;

		// Shading recompute only exists for in-house simulation designs — nothing to re-request
		// (and no shadingAnalysis worth trusting) in any other mode.
		if (!isInHouseSimulationMode(state.roofData.data.mode)) {
			markNoShadingRunExpected();
			return;
		}

		const analysis = readShadingAnalysis(action.payload);

		if (!analysis) {
			markNoShadingRunExpected();
			return;
		}

		// The design owes a shading run: 
		// the DSM version has moved on from whatever shading last ran against.
		const isOwed = analysis.dsmVersion !== analysis.shadingVersion;
		// The server believes a run is already enqueued/running (QUEUED/RUNNING).
		const inFlight = isWatchableStatus(analysis.shadingStatus);
		// An owed run that was never fired and the tab closed before
		// "Done".
		const isAbandoned = analysis.shadingStatus === SHADING_STATUS.PENDING
			&& fetchRoofData.fulfilled.match(action);

		if (!isOwed) {
			if (inFlight) api.dispatch(resumeShadingWatch());
			else markShadingComplete({
				status: analysis.shadingStatus,
				inSync: true,
				note: 'already complete when the design fetch resolved',
			});
			return;
		}

		// Only re-request when a run is owed, and only for the two states that mean nothing is
		// coming on its own: the QUEUED/RUNNING case this listener exists for (a possibly-lost
		// job), or a PENDING run abandoned by a closed tab. COMPLETED + !inSync is the normal
		// state after edits with no Done — the user has not asked for a run, so don't fire one.
		const shouldRerequest = inFlight || isAbandoned;
		if (!shouldRerequest) return;

		// Which script this lands on is `dsmDirty`'s call, made API-side by the one endpoint:
		// unbaked DSM edits → the full bake, otherwise the shorter facet rebuild. The 202 echoes
		// the pick back as `runType`, so there is nothing to route from here.
		const uuid = state.roofData.data.uuid;
		if (!uuid) return;

		api.dispatch(shadingRecalcRequested()); // spinner + isRecomputing while the POST is in flight
		try {
			api.dispatch(shadingRecalcAccepted(await recalculateShading(uuid)));
		} catch {
			api.dispatch(resumeShadingWatch()); // POST failed — still watch the run the server reported
		}
	},
});

dsmShadingListener.startListening({
	actionCreator: resetDsmShading,
	effect: (_a, api) => api.cancelActiveListeners(),
});
